import { memo } from 'react';

type IconProps = { size?: number; color?: string };

/**
 * Web twin of SuggestionIcons.tsx, which renders through react-native-svg's
 * SvgXml and therefore cannot run here. Same Figma paths and same default
 * tints, emitted as plain inline SVG so the chat empty-state pills look
 * identical on web and native.
 *
 * The native TailorCV glyph carries a full-bounds <clipPath>; it clips nothing
 * inside an 18x18 viewBox and a fixed id would collide across instances, so it
 * is dropped here rather than duplicated.
 */

// ─── Job (Briefcase) ──────────────────────────────────────────────────
export const JobIcon = memo(function JobIcon({
  size = 18,
  color = '#7986FE',
}: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" aria-hidden>
      <path d="M11.75 5.5C11.336 5.5 11 5.164 11 4.75V2.25C11 2.112 10.888 2 10.75 2H7.25C7.112 2 7 2.112 7 2.25V4.75C7 5.164 6.664 5.5 6.25 5.5C5.836 5.5 5.5 5.164 5.5 4.75V2.25C5.5 1.285 6.285 0.5 7.25 0.5H10.75C11.715 0.5 12.5 1.285 12.5 2.25V4.75C12.5 5.164 12.164 5.5 11.75 5.5Z" fill={color} />
      <path d="M17 7.519V6.75C17 5.233 15.767 4 14.25 4H3.75C2.233 4 1 5.233 1 6.75V7.519C3.184 8.668 5.643 9.349 8.25 9.462V9.25C8.25 8.836 8.586 8.5 9 8.5C9.414 8.5 9.75 8.836 9.75 9.25V9.462C12.357 9.349 14.816 8.668 17 7.519Z" fill={color} />
      <path d="M9.75 10.9616V11.4996C9.75 11.9136 9.414 12.2496 9 12.2496C8.586 12.2496 8.25 11.9136 8.25 11.4996V10.9616C5.664 10.8586 3.216 10.2336 1 9.18164V13.2496C1 14.7666 2.233 15.9996 3.75 15.9996H14.25C15.767 15.9996 17 14.7666 17 13.2496V9.18164C14.784 10.2326 12.336 10.8586 9.75 10.9616Z" fill={color} />
    </svg>
  );
});

// ─── CV (File) ────────────────────────────────────────────────────────
export const CVIcon = memo(function CVIcon({
  size = 18,
  color = '#A379FE',
}: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" aria-hidden>
      <path d="M15.487 5.427L11.573 1.513C11.242 1.182 10.803 1 10.336 1H4.75C3.233 1 2 2.233 2 3.75V14.25C2 15.767 3.233 17 4.75 17H13.25C14.767 17 16 15.767 16 14.25V6.664C16 6.197 15.818 5.757 15.487 5.427ZM5.75 6H7.75C8.164 6 8.5 6.336 8.5 6.75C8.5 7.164 8.164 7.5 7.75 7.5H5.75C5.336 7.5 5 7.164 5 6.75C5 6.336 5.336 6 5.75 6ZM12.25 13.5H5.75C5.336 13.5 5 13.164 5 12.75C5 12.336 5.336 12 5.75 12H12.25C12.664 12 13 12.336 13 12.75C13 13.164 12.664 13.5 12.25 13.5ZM12.25 10.5H5.75C5.336 10.5 5 10.164 5 9.75C5 9.336 5.336 9 5.75 9H12.25C12.664 9 13 9.336 13 9.75C13 10.164 12.664 10.5 12.25 10.5ZM14.432 6.5H11.5C10.95 6.5 10.5 6.05 10.5 5.5V2.579L10.513 2.574L14.435 6.495L14.432 6.5Z" fill={color} />
    </svg>
  );
});

// ─── Tailor CV (Edit-pen on file) ─────────────────────────────────────
export const TailorCVIcon = memo(function TailorCVIcon({
  size = 18,
  color = '#79D2FE',
}: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" aria-hidden>
      <path d="M17.3625 11.2221L17.0271 10.8867C16.3669 10.2251 15.2141 10.2256 14.552 10.8862L11.3908 14.0473C11.3102 14.1279 11.2492 14.2255 11.2121 14.3329L10.2912 17.0038C10.1974 17.2753 10.2668 17.5761 10.4699 17.7787C10.613 17.9218 10.8044 17.9984 11.0002 17.9984C11.0822 17.9984 11.1648 17.9852 11.2448 17.9574L13.9157 17.0365C14.0231 16.9994 14.1208 16.9384 14.2013 16.8578L17.3624 13.6967C17.693 13.3661 17.8751 12.9267 17.8751 12.4594C17.8751 11.9916 17.6931 11.5522 17.3625 11.2221Z" fill={color} />
      <path fillRule="evenodd" clipRule="evenodd" d="M8.8736 16.5136C8.81853 16.6732 8.78227 16.8363 8.76417 17H4.75C3.233 17 2 15.767 2 14.25V3.74902C2 2.23202 3.233 0.999023 4.75 0.999023H10.335C10.802 0.999023 11.241 1.18102 11.572 1.51202L15.486 5.42602C15.817 5.75602 15.999 6.19602 15.999 6.66302V8.89668C15.0937 8.84071 14.1676 9.15057 13.4927 9.82397L10.3303 12.9863C10.0889 13.2277 9.90579 13.5205 9.79447 13.8428L8.8736 16.5136ZM5.75 5.99902H7.75C8.164 5.99902 8.5 6.33502 8.5 6.74902C8.5 7.16302 8.164 7.49902 7.75 7.49902H5.75C5.336 7.49902 5 7.16302 5 6.74902C5 6.33502 5.336 5.99902 5.75 5.99902ZM5 9.74902C5 9.33502 5.336 8.99902 5.75 8.99902H10.25C10.664 8.99902 11 9.33502 11 9.74902C11 10.163 10.664 10.499 10.25 10.499H5.75C5.336 10.499 5 10.163 5 9.74902ZM11.501 6.499C10.951 6.499 10.501 6.049 10.501 5.499L10.5 2.578L14.433 6.499H11.501Z" fill={color} />
    </svg>
  );
});

// ─── Interview (Headphones) ───────────────────────────────────────────
export const InterviewIcon = memo(function InterviewIcon({
  size = 18,
  color = '#799FFE',
}: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" aria-hidden>
      <path d="M4.7754 13.0001H2.75C2.0605 13.0001 1.5 12.4396 1.5 11.7501V7.4898C1.5 5.8423 2.1782 4.2422 3.3613 3.1006C4.4839 2.0176 5.9541 1.4551 7.456 1.5039C9.4277 1.5732 11.204 2.6479 12.2084 4.3784C12.4159 4.7368 12.2943 5.1958 11.9359 5.4038C11.577 5.6099 11.1185 5.4892 10.9105 5.1313C10.1673 3.8496 8.8558 3.0542 7.4032 3.0029C6.3002 2.9717 5.2299 3.3818 4.4027 4.1801C3.5111 5.0405 2.9999 6.247 2.9999 7.4897V11.5H4.7753C5.1894 11.5 5.5253 11.8359 5.5253 12.25C5.5253 12.6641 5.1895 13.0001 4.7754 13.0001Z" fill={color} />
      <path d="M14.9298 8.29992C13.9889 7.40442 12.777 6.93522 11.5143 7.00602C9.06408 7.12612 7.12558 9.06462 7.00548 11.5148C6.94448 12.7589 7.39418 13.9513 8.27108 14.8722C9.25648 15.9069 10.7013 16.5001 12.2359 16.5001H15.2535C15.9405 16.5001 16.4996 15.9396 16.4996 15.2501V11.9679C16.4996 10.587 15.9273 9.25012 14.9298 8.29992Z" fill={color} />
    </svg>
  );
});

// The three empty-state starters below come from the chat home design (node
// 1:185), where they are 16px feather-weight outlines in the text-2 grey
// rather than the filled, tinted glyphs above. Paths are the Figma exports.

// ─── Find roles (Briefcase, outline) ──────────────────────────────────
export const FindRolesIcon = memo(function FindRolesIcon({
  size = 16,
  color = '#4A4A4D',
}: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <g stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
        <path d="M1.55556 7.77778C2.81867 8.32533 5.08089 9.11378 8 9.11378C10.9191 9.11378 13.1813 8.32533 14.4444 7.77778" />
        <path d="M5.55556 4.22222V2C5.55556 1.50933 5.95378 1.11111 6.44444 1.11111H9.55556C10.0462 1.11111 10.4444 1.50933 10.4444 2V4.22222" />
        <path d="M12.6667 4.22222H3.33333C2.35149 4.22222 1.55556 5.01816 1.55556 6V11.7778C1.55556 12.7596 2.35149 13.5556 3.33333 13.5556H12.6667C13.6485 13.5556 14.4444 12.7596 14.4444 11.7778V6C14.4444 5.01816 13.6485 4.22222 12.6667 4.22222Z" />
      </g>
    </svg>
  );
});

// ─── LinkedIn ─────────────────────────────────────────────────────────
// The export wraps these in a full-bounds <clipPath>; it clips nothing at this
// viewBox and a fixed id would collide across instances, so it is dropped.
export const LinkedInIcon = memo(function LinkedInIcon({
  size = 16,
  color = '#4A4A4D',
}: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M4.33333 5.33333C4.88562 5.33333 5.33333 4.88562 5.33333 4.33333C5.33333 3.78105 4.88562 3.33333 4.33333 3.33333C3.78105 3.33333 3.33333 3.78105 3.33333 4.33333C3.33333 4.88562 3.78105 5.33333 4.33333 5.33333Z" fill={color} />
      <path d="M3.33333 6.66667C3.33333 6.29848 3.63181 6 4 6H4.66667C5.03485 6 5.33333 6.29847 5.33333 6.66667V12C5.33333 12.3682 5.03485 12.6667 4.66667 12.6667H4C3.63181 12.6667 3.33333 12.3682 3.33333 12V6.66667Z" fill={color} />
      <path d="M7.33333 12.6667H8C8.3682 12.6667 8.66667 12.3682 8.66667 12V9C8.66667 8 10.6667 7.33333 10.6667 8.66667V12.0003C10.6667 12.3685 10.9651 12.6667 11.3333 12.6667H12C12.3682 12.6667 12.6667 12.3682 12.6667 12V8C12.6667 6.66667 11.6667 6 10.3333 6C9 6 8.66667 7 8.66667 7V6.66667C8.66667 6.29847 8.3682 6 8 6H7.33333C6.96513 6 6.66667 6.29848 6.66667 6.66667V12C6.66667 12.3682 6.96513 12.6667 7.33333 12.6667Z" fill={color} />
      <path fillRule="evenodd" clipRule="evenodd" d="M13.3333 0.666667C14.4379 0.666667 15.3333 1.5621 15.3333 2.66667V13.3333C15.3333 14.4379 14.4379 15.3333 13.3333 15.3333H2.66667C1.5621 15.3333 0.666667 14.4379 0.666667 13.3333V2.66667C0.666667 1.5621 1.5621 0.666667 2.66667 0.666667H13.3333ZM13.3333 2C13.7015 2 14 2.29848 14 2.66667V13.3333C14 13.7015 13.7015 14 13.3333 14H2.66667C2.29848 14 2 13.7015 2 13.3333V2.66667C2 2.29848 2.29848 2 2.66667 2H13.3333Z" fill={color} />
    </svg>
  );
});

// ─── Industry briefing (File-text, outline) ───────────────────────────
export const BriefingIcon = memo(function BriefingIcon({
  size = 16,
  color = '#4A4A4D',
}: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <g stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
        <path d="M9.33333 1.33333H4C3.64638 1.33333 3.30724 1.47381 3.05719 1.72386C2.80714 1.97391 2.66667 2.31304 2.66667 2.66667V13.3333C2.66667 13.687 2.80714 14.0261 3.05719 14.2761C3.30724 14.5262 3.64638 14.6667 4 14.6667H12C12.3536 14.6667 12.6928 14.5262 12.9428 14.2761C13.1929 14.0261 13.3333 13.687 13.3333 13.3333V5.33333L9.33333 1.33333Z" />
        <path d="M9.33333 1.33333V5.33333H13.3333" />
        <path d="M10.6667 8.66667H5.33333" />
        <path d="M10.6667 11.3333H5.33333" />
        <path d="M6.66667 6H6H5.33333" />
      </g>
    </svg>
  );
});
